<script setup>
import { deleteRequest, getRequest, request } from "@/utils/request.js";
import {
  ImportOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons-vue";
import { message } from "ant-design-vue";
import { h, onMounted, reactive, ref, unref } from "vue";
import JobTypeFormModal from "@/views/jobType/modules/JobTypeFormModal.vue";

const formRef = ref();
const searchFormModel = ref({});
const datasource = ref([]);
const loading = ref(false);
const modalRef = ref();
const columns = [
  {
    key: "name",
    dataIndex: "name",
    title: "名称",
    width: "20%",
  },
  {
    key: "type",
    dataIndex: "type",
    title: "类型",
    width: "15%",
  },
  {
    key: "sort",
    dataIndex: "sort",
    title: "排序",
    width: "3%",
  },
  {
    key: "action",
    dataIndex: "action",
    title: "操作",
    width: "300px",
  },
];

const pagination = reactive({
  current: 1,
  pageSize: 10,
  pageSizeOptions: ["10", "50", "100"],
  showTotal: (total, range) => {
    return range[0] + "-" + range[1] + " 共" + total + "条";
  },
  showQuickJumper: true,
  showSizeChanger: true,
  total: 0,
});

const loadData = () => {
  loading.value = true;
  const { current, pageSize } = unref(pagination);
  const requestBody = Object.assign({}, unref(searchFormModel), {
    pageNo: current,
    pageSize,
  });
  getRequest("/jobType", requestBody)
    .then((response) => {
      if (response.success) {
        datasource.value = response.data;
      }
    })
    .finally(() => {
      loading.value = false;
    });
};

const resetForm = () => {
  formRef.value.resetFields();
  loadData();
};

const handleAdd = (record) => {
  modalRef.value.title = "工种新增";
  modalRef.value.handleAdd(record);
};

const handleEdit = (record) => {
  modalRef.value.title = "工种编辑";
  modalRef.value.handleEdit(record);
};

const handleDelete = (record) => {
  deleteRequest(`/jobType/${record.id}`).then((response) => {
    if (response.success) {
      message.success("删除成功");
      loadData();
    } else {
      message.error("删除失败。" + response.message);
    }
  });
};

const tableChange = ({ current, pageSize }) => {
  pagination.current = current;
  pagination.pageSize = pageSize;
  loadData();
};

onMounted(() => {
  loadData();
});
</script>

<template>
  <div>
    <a-card>
      <a-form
        ref="formRef"
        layout="inline"
        :labelCol="{ span: 3, offset: 1 }"
        :model="searchFormModel"
        @keydown.enter="loadData"
      >
        <a-row :gutter="[10, 10]" style="width: 100%">
          <a-col :span="6">
            <a-form-item label="名称" name="name">
              <a-input
                v-model:value="searchFormModel.name"
                placeholder="请输入菜单名称"
              />
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="类型" name="type">
              <a-select
                v-model:value="searchFormModel.type"
                placeholder="请选择类型"
              >
                <a-select-option value="BIG_CATEGORY"> 大类 </a-select-option>
                <a-select-option value="SMALL_CATEGORY"> 子类 </a-select-option>
                <a-select-option value="JOB"> 工种 </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="24">
            <a-button
              type="primary"
              style="margin-left: 18px"
              :icon="h(SearchOutlined)"
              @click="loadData"
              @keydown.enter="loadData"
            >
              查询
            </a-button>
            <a-button
              style="margin-left: 18px"
              :icon="h(ReloadOutlined)"
              @click="resetForm"
              >重置
            </a-button>
          </a-col>
        </a-row>
      </a-form>
    </a-card>

    <a-card style="margin-top: 10px">
      <div style="margin-bottom: 12px; display: flex; gap: 18px">
        <a-button type="primary" @click="handleAdd({})" :icon="h(PlusOutlined)"
          >新增
        </a-button>
        <a-upload
          :disabled="uploading"
          :show-upload-list="false"
          ref="upload"
          :customRequest="fileUpload"
          accept=".xlsx, .xls, .csv"
        >
          <a-button
            type="primary"
            :icon="h(ImportOutlined)"
            :loading="uploading"
            >导入
          </a-button>
        </a-upload>
      </div>
      <a-table
        row-key="id"
        :loading="loading"
        :columns="columns"
        :data-source="datasource"
        :bordered="true"
        :pagination="false"
        @change="tableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'type'">
            <template v-if="record[column.dataIndex] === 'BIG_CATEGORY'">
              <a-tag color="orange">大类</a-tag>
            </template>
            <template v-else-if="record[column.dataIndex] === 'SMALL_CATEGORY'">
              <a-tag color="blue">子类</a-tag>
            </template>
            <template v-else-if="record[column.dataIndex] === 'JOB'">
              <a-tag color="green">工种</a-tag>
            </template>
            <template v-else>
              {{ record[column.dataIndex] }}
            </template>
          </template>

          <template v-if="column.dataIndex === 'action'">
            <a type="link" @click="handleEdit(record)">编辑</a>
            <a-divider type="vertical" />
            <a-popconfirm title="确认要删除吗?" @confirm="handleDelete(record)">
              <a type="link">删除</a>
            </a-popconfirm>
            <template
              v-if="['BIG_CATEGORY', 'SMALL_CATEGORY'].includes(record['type'])"
            >
              <a-divider type="vertical" />
              <a
                type="link"
                @click="
                  handleAdd({ parentId: record.id, parentType: record.type })
                "
                >增加下级菜单</a
              >
            </template>
          </template>
        </template>
      </a-table>
    </a-card>
    <job-type-form-modal ref="modalRef" @ok="loadData" />
  </div>
</template>

<style scoped lang="scss"></style>
